import _ from 'lodash';
import cookie from 'js-cookie';
import config from 'client/shared/config';

import {
	observable,
	action,
	computed,
	reaction,
	runInAction
} from 'mobx';

import { signIn, signOut } from 'aws-amplify/auth';
import { clearAuthCookies } from 'client/shared/util';

import BaseStore from './base-store';
import apiStore from './api-store';
import User from './user';
// Note: circular import (artist-store also imports user-store). Safe because
// both default exports are instances created at module init, and we only
// reference artistStore from inside async methods that run post-init.
import artistStore from './artist-store';

const DEV_ROUTE_MAP = {
	'http://int.artists.muzado.com': '/artist/',
	'http://int.mzdadm.muzado.com': '/admin/',
	'http://int.members.muzado.com': '/members/'
};

class UserStore extends BaseStore {
	apiRoute = config.getRoute('users');

	fetchPagedItemsRoute = config.getRoute('usersPaged');

	roles = {
		ADMIN: 'ADMIN',
		ARTIST: 'ARTIST',
		SUBSCRIBER: 'SUBSCRIBER'
	};

	constructor () {
		super();

		this.disposeReaction = reaction(
			() => apiStore.data.user,
			this.onFetchSuccess
		);
	}

	destroy () {
		this.disposeReaction();
	}

	/**
	 * Observables & Computeds
	 */

 	@observable
 	_addMemberModalOpen = false;

	@computed
	get addMemberModalOpen () {
		return this._addMemberModalOpen;
	}

	set addMemberModalOpen (addMemberModalOpen) {
		this._addMemberModalOpen = addMemberModalOpen;
	}

	@observable
	_newFBUserData = {};

	@computed
	get newFBUserData () {
		return this._newFBUserData;
	}

	set newFBUserData (newFBUserData) {
		this._newFBUserData = newFBUserData;
	}

	@observable
	_loginError = false;

	@computed
	get loginError () {
		return this._loginError;
	}

	set loginError (loginError) {
		this._loginError = loginError;
	}

	@observable
	_loginSuccess = false;

	@computed
	get loginSuccess () {
		return this._loginSuccess;
	}

	set loginSuccess (loginSuccess) {
		this._loginSuccess = loginSuccess;
	}

	@observable
	_isSubscribing = false;

	@computed
	get isSubscribing () {
		return this._isSubscribing;
	}

	set isSubscribing (isSubscribing) {
		this._isSubscribing = isSubscribing;
	}

	@computed
	get users () {
		return this._items;
	}

	set users (users) {
		this._items = users;
	}

	@computed
	get subscriberList () {
		return _(this.users)
			.filter(({ role }) => role === this.roles.SUBSCRIBER)
			.sortBy('createdAt')
			.value();
	}

	@computed
	get artistList () {
		return this.users
			.filter(({ role }) => role === this.roles.ARTIST);
	}

	/**
	 * Actions
	 */

	@action.bound
	onFetchSuccess (data) {
		this.isLoading = false;

		if (_.isEmpty(data)) {

			return;
		}

		const userData = _.isArray(data) ? data : [ data ];

		this.users = userData.map((user) => {
			const { id } = user;
			const existing = this.getItemByID(id);

			if (_.isEmpty(existing)) {

				return new User(this, user);
			}

			existing.update(user);
			return existing;
		});
	}

	@action
	newUser () {
		const user = new User(this);
		this.users.unshift(user);

		return user;
	}

	@action
	async saveUser (user) {
		const route = _.isEmpty(user.id) ? config.getRoute('createUser') : this.apiRoute;
		await this.save(user, { route });
		return user;
	}

	/**
	 * Surgical PATCH — sends only the fields you pass. Avoids the bug where
	 * full-payload saves transmit empty strings for unset fields like
	 * `gender` (enum) and trip mongoose validation, taking down the API.
	 *
	 * @param {string} id     User ObjectId
	 * @param {Object} fields Subset of fields to update
	 */
	@action
	async patchUser (id, fields) {
		if (_.isEmpty(id) || _.isEmpty(fields)) {
			return;
		}
		const response = await apiStore.save({
			route: this.apiRoute,
			payload: { _id: id, ...fields },
			method: 'patch',
		});

		// API attaches `linkedArtistSearchStatus` when a profileImage patch
		// would otherwise leave the artist's searchStatus stale.
		if (response?.linkedArtistSearchStatus) {
			artistStore.applyLinkedArtistSearchStatus(response.linkedArtistSearchStatus);
		}

		return response;
	}

	@action
	async confirmUser (data) {
		const route = config.getRoute('confirmUser');
		return await this.save(data, { route, method: 'post' });
	}

	@action.bound
	async saveProfileImage (user) {
		const { id, profileImageData, userNameForFilePath } = user;
		const route = config.getRoute('profileImageUpload', id);
		const payload = {
			fileName: 'profile-image',
			imageData: profileImageData,
			path: `profile-images/${id}`
		};

		try {

			const response = await this.save(payload, { route, silent: false });
			const { path, searchStatus } = response || {};
			console.log('[user-store->saveProfileImage] path', path);
			runInAction('Update with saved profile image', () => {
				user.profileImage = path;
				user._profileImageCacheBust = Date.now();
			});

			// API attaches the freshly-recomputed searchStatus on profile
			// image upload (the trigger handles user→artist sync async; the
			// inline refresh closes the UI gap).
			if (searchStatus) {
				artistStore.applyLinkedArtistSearchStatus({
					artistID: artistStore.loggedInArtist?.id,
					searchStatus,
				});
			}
		} catch (err) {

			console.error('[ERROR: UserStore.saveProfileImage]', err);
		}
	}

	@action
	async removeProfileImage (user) {
		const route = config.getRoute('removeProfileImage', user.id);

		try {

			await apiStore.delete({ route });
			user.profileImage = '';
		} catch (err) {

			console.error('[ERROR: UserStore.removeProfileImage]', err);
		}
	}

	@action
	async cancelSubscription (subscriptionId) {
		try {
			const route = `${config.getRoute('subscription')}/${subscriptionId}`;
			await this.delete(null, { route });
			await this.fetchProfile({ silent: true });
		} catch (err) {
			console.error('[ERROR: cancelSubscription]', err);
			throw err;
		}
	}

	@action
	async checkLogin (user, opts = { sendRedirect: true, silent: true }) {
		this.isLoading = true;

		try {

			const { userName, password } = _.isEmpty(user) ? opts : user;

			if (_.isEmpty(userName) || _.isEmpty(password)) {

				throw new Error('No login credentials found.');
			}

			try { await signOut({ global: true }); } catch (_) { /* no active session */ }
			await signIn({
				username: userName,
				password,
				options: {
					authFlowType: 'USER_PASSWORD_AUTH'
				}
			});

			this.loginSuccess = true;

			const profile = await this.fetchProfile({ silent: opts.silent });

			if (!profile) {
				throw new Error('Could not load user profile after login.');
			}

			const { routeTo } = profile;

			if (opts.sendRedirect) {

				window.location.href = import.meta.env.VITE_PROXY_TO_INT === 'true' ?
					DEV_ROUTE_MAP[routeTo] : routeTo;
			}
		} catch (err) {

			const { name, underlyingError, recoverySuggestion } = err;

			console.error('[ERROR: UserStore.checkLogin]', name, underlyingError, recoverySuggestion);
			console.error('[ERROR: UserStore.checkLogin]', err);
			this.loginError = true;
		} finally {

			this.isLoading = false;
		}
	}

	@action
	async logout () {
		this.isLoading = true;

		try {

			await signOut({ global: true});
		} catch (err) {

			console.error('[ERROR: UserStore.logout]', err);
		} finally {

			this.isLoading = false;

			// Amplify's signOut only reliably clears cookies it wrote for the
			// current subdomain — Cognito cookies land on `.muzado.com` and
			// leak between artist / member / admin subdomains. Explicit
			// cleanup makes "logged out" actually stick across subdomains
			// (and also clears the API's koa session cookie).
			clearAuthCookies();

			// Defer the navigation by a tick so in-flight HTTP/2 streams on
			// the muzado.com connection have a chance to finish closing. The
			// post-logout redirect is cross-subdomain (e.g. artists.muzado.com
			// → muzado.com) and Chrome coalesces those onto the same H2
			// connection; navigating immediately would race onto a stream
			// that was still tearing down and produce ERR_CONNECTION_CLOSED.
			// `replace` so the broken state isn't in back-button history.
			setTimeout(() => {
				window.location.replace(config.logoutPageURI);
			}, 0);
		}
	}

	@action
	async fetchProfile (opts = {}) {
		const { silent = false } = opts;

		const route = config.getRoute('profile');
		this.isLoading = silent !== true;

		try {

			const profile = await apiStore.fetch({ route });
			apiStore.userID = profile.user.id;
			this.onFetchSuccess(profile.user);
			return profile;
		} catch (err) {

			console.error('[ERROR: BaseStore.fetch]', err);
		} finally {

			this._isLoading = false;
		}
	}

	/**
	 * Methods
	 */

	getLoggedInUser () {
		return this.getItemByID(apiStore.userID);
	}

	async checkEmail (email) {
		try {

			const silent = true;
			const returnData = true;
			const route = config.getRoute('checkEmail').replace(/\:email$/, email);

			return await this.fetch({ route, returnData, silent });
		} catch (err) {

			console.error('[ERROR: UserStore.checkEmail]', err);
		}
	}
}

const userStore = new UserStore();
export default userStore;
